import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

type MerchantConnection = Tables<'merchant_connections'>;

const STORAGE_KEY = 'selected_merchant_id';
const URL_PARAM = 'merchant';

const slugify = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

interface MerchantContextType {
  selectedMerchantId: string | null;
  setSelectedMerchantId: (id: string | null) => void;
  merchants: MerchantConnection[];
  loading: boolean;
  refreshMerchants: () => Promise<void>;
}

const MerchantContext = createContext<MerchantContextType | undefined>(undefined);

export const MerchantProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMerchantId, setSelectedMerchantIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });
  const [merchants, setMerchants] = useState<MerchantConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const urlSyncedRef = useRef(false);

  // Refs so callbacks can read latest searchParams without re-creating.
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
    setSearchParamsRef.current = setSearchParams;
  }, [searchParams, setSearchParams]);

  const writeMerchantToUrl = useCallback((slug: string | null) => {
    const current = searchParamsRef.current;
    const existing = current.get(URL_PARAM);
    if (slug && existing === slug) return;
    if (!slug && !existing) return;
    const next = new URLSearchParams(current);
    if (slug) next.set(URL_PARAM, slug);
    else next.delete(URL_PARAM);
    setSearchParamsRef.current(next, { replace: true });
  }, []);

  const setSelectedMerchantId = useCallback((id: string | null) => {
    setSelectedMerchantIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
    // Update URL in the same tick as the state change so the URL→selection
    // effect never sees a stale URL and reverts the selection.
    const merchant = id ? merchants.find((m) => m.id === id) : null;
    writeMerchantToUrl(merchant ? slugify(merchant.merchant_name) : null);
  }, [merchants, writeMerchantToUrl]);

  const fetchMerchants = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('merchant_connections')
        .select('*')
        .eq('is_active', true)
        .order('merchant_name');

      if (error) throw error;

      const merchantList = data || [];
      setMerchants(merchantList);

      // 1) URL has highest priority on first load
      const urlSlug = searchParamsRef.current.get(URL_PARAM);
      if (urlSlug && !urlSyncedRef.current) {
        const fromUrl = merchantList.find(
          (m) => slugify(m.merchant_name) === urlSlug || m.id === urlSlug
        );
        if (fromUrl) {
          urlSyncedRef.current = true;
          setSelectedMerchantIdState(fromUrl.id);
          localStorage.setItem(STORAGE_KEY, fromUrl.id);
          return;
        }
      }

      // 2) Validate stored selection - clear if merchant no longer exists
      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId && !merchantList.find((m) => m.id === storedId)) {
        localStorage.removeItem(STORAGE_KEY);
        setSelectedMerchantIdState(null);
      }

      // 3) Auto-select if none selected
      if (!selectedMerchantId && merchantList.length > 0) {
        const validStoredId = storedId && merchantList.find((m) => m.id === storedId);
        if (validStoredId) {
          setSelectedMerchantIdState(storedId);
        } else if (merchantList.length === 1) {
          setSelectedMerchantIdState(merchantList[0].id);
          localStorage.setItem(STORAGE_KEY, merchantList[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync when the selection or merchant list changes
  // (covers initial auto-selection after fetchMerchants resolves).
  useEffect(() => {
    if (loading || merchants.length === 0) return;
    const selected = merchants.find((m) => m.id === selectedMerchantId);
    writeMerchantToUrl(selected ? slugify(selected.merchant_name) : null);
  }, [selectedMerchantId, merchants, loading, writeMerchantToUrl]);

  // Sync URL -> selection only for external URL changes (back/forward,
  // shared link). Compare against the current selection's slug to avoid
  // reacting to URL updates we just wrote ourselves.
  useEffect(() => {
    if (loading || merchants.length === 0) return;
    const urlSlug = searchParams.get(URL_PARAM);
    if (!urlSlug) return;
    const selected = merchants.find((m) => m.id === selectedMerchantId);
    const currentSlug = selected ? slugify(selected.merchant_name) : null;
    if (urlSlug === currentSlug) return;
    const fromUrl = merchants.find(
      (m) => slugify(m.merchant_name) === urlSlug || m.id === urlSlug
    );
    if (fromUrl && fromUrl.id !== selectedMerchantId) {
      setSelectedMerchantIdState(fromUrl.id);
      localStorage.setItem(STORAGE_KEY, fromUrl.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, merchants, loading]);

  // Listen for auth state changes and clear merchant selection on sign out
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        localStorage.removeItem(STORAGE_KEY);
        setSelectedMerchantIdState(null);
        setMerchants([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <MerchantContext.Provider value={{ selectedMerchantId, setSelectedMerchantId, merchants, loading, refreshMerchants: fetchMerchants }}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchant = () => {
  const context = useContext(MerchantContext);
  if (context === undefined) {
    throw new Error('useMerchant must be used within a MerchantProvider');
  }
  return context;
};
